import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './input';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { RotateCcw, Pipette } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

// Convert HSV to RGB
const hsvToRgb = (h, s, v) => {
  let r, g, b;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
    default: r = 0; g = 0; b = 0;
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

// Convert RGB to HSV
const rgbToHsv = (r, g, b) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (max !== min) {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return [h, s, v];
};

// Convert HEX to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
};

// Convert RGB to HEX
const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('').toUpperCase();
};

export function ColorPicker({ 
  label, 
  value, 
  onChange, 
  defaultValue,
  className = '' 
}) {
  const { adminDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [hexInput, setHexInput] = useState(value || defaultValue || '#000000');
  
  // HSV state
  const [hsv, setHsv] = useState(() => {
    const rgb = hexToRgb(value || defaultValue || '#000000');
    return rgbToHsv(...rgb);
  });

  // RGB state for display
  const [rgb, setRgb] = useState(() => hexToRgb(value || defaultValue || '#000000'));

  const pickerRef = useRef(null);
  const hueRef = useRef(null);
  const isDraggingPicker = useRef(false);
  const isDraggingHue = useRef(false);

  useEffect(() => {
    const rgbVal = hexToRgb(value || defaultValue || '#000000');
    const newHsv = rgbToHsv(...rgbVal);
    setHsv(newHsv);
    setRgb(rgbVal);
    setHexInput(value || defaultValue || '#000000');
  }, [value, defaultValue]);

  const updateColor = useCallback((newHsv) => {
    setHsv(newHsv);
    const rgbVal = hsvToRgb(...newHsv);
    setRgb(rgbVal);
    const hex = rgbToHex(...rgbVal);
    setHexInput(hex);
    onChange(hex);
  }, [onChange]);

  const handlePickerMouseDown = (e) => {
    isDraggingPicker.current = true;
    handlePickerMove(e);
  };

  const handlePickerMove = useCallback((e) => {
    if (!pickerRef.current) return;
    const rect = pickerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    updateColor([hsv[0], x, 1 - y]);
  }, [hsv, updateColor]);

  const handleHueMouseDown = (e) => {
    isDraggingHue.current = true;
    handleHueMove(e);
  };

  const handleHueMove = useCallback((e) => {
    if (!hueRef.current) return;
    const rect = hueRef.current.getBoundingClientRect();
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    updateColor([y, hsv[1], hsv[2]]);
  }, [hsv, updateColor]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingPicker.current) handlePickerMove(e);
      if (isDraggingHue.current) handleHueMove(e);
    };

    const handleMouseUp = () => {
      isDraggingPicker.current = false;
      isDraggingHue.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handlePickerMove, handleHueMove]);

  const handleHexInputChange = (e) => {
    let val = e.target.value.toUpperCase();
    if (!val.startsWith('#')) val = '#' + val;
    setHexInput(val);
    
    if (/^#[0-9A-F]{6}$/i.test(val)) {
      const rgbVal = hexToRgb(val);
      const newHsv = rgbToHsv(...rgbVal);
      setHsv(newHsv);
      setRgb(rgbVal);
      onChange(val);
    }
  };

  const handleHexInputBlur = () => {
    // On blur, if invalid, reset to current value
    if (!/^#[0-9A-F]{6}$/i.test(hexInput)) {
      setHexInput(value || defaultValue || '#000000');
    }
  };

  const handleRgbChange = (index, newValue) => {
    const val = Math.max(0, Math.min(255, parseInt(newValue) || 0));
    const newRgb = [...rgb];
    newRgb[index] = val;
    setRgb(newRgb);
    const hex = rgbToHex(...newRgb);
    setHexInput(hex);
    const newHsv = rgbToHsv(...newRgb);
    setHsv(newHsv);
    onChange(hex);
  };

  const handleReset = (e) => {
    e.stopPropagation();
    const rgbVal = hexToRgb(defaultValue);
    const newHsv = rgbToHsv(...rgbVal);
    setHsv(newHsv);
    setRgb(rgbVal);
    setHexInput(defaultValue);
    onChange(defaultValue);
  };

  // Current hue color for the picker background
  const hueColor = rgbToHex(...hsvToRgb(hsv[0], 1, 1));
  const currentColor = value || defaultValue;

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <div className="flex items-center justify-between">
          <label className={`text-sm font-medium ${adminDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{label}</label>
          {defaultValue && value !== defaultValue && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className={`h-6 px-2 text-xs ${adminDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset
            </Button>
          )}
        </div>
      )}
      
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div className="flex items-center gap-2 cursor-pointer group">
            {/* Color Swatch */}
            <div
              className={`w-12 h-10 rounded-lg border-2 cursor-pointer transition-all shadow-inner flex items-center justify-center ${adminDarkMode ? 'border-slate-600 group-hover:border-slate-400' : 'border-slate-300 group-hover:border-slate-400'}`}
              style={{ backgroundColor: currentColor }}
            >
              <Pipette className="h-4 w-4 text-white/50 opacity-0 group-hover:opacity-100 transition-opacity" style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))' }} />
            </div>
            
            {/* Hex Input - Editable */}
            <input
              type="text"
              value={hexInput}
              onChange={handleHexInputChange}
              onBlur={handleHexInputBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className={`flex-1 px-3 py-2 rounded-md font-mono text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[var(--admin-accent)]/50 ${adminDarkMode ? 'bg-slate-800/50 border border-slate-700 text-white' : 'bg-white border border-slate-300 text-slate-900'}`}
              placeholder="#000000"
              maxLength={7}
            />
          </div>
        </PopoverTrigger>
        
        <PopoverContent className="w-72 p-4 bg-slate-900 border-slate-700" align="start">
          <div className="flex gap-3">
            {/* Saturation/Brightness Picker */}
            <div
              ref={pickerRef}
              className="w-52 h-52 rounded-lg cursor-crosshair relative"
              style={{
                background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueColor})`,
              }}
              onMouseDown={handlePickerMouseDown}
            >
              {/* Picker Handle */}
              <div
                className="absolute w-5 h-5 rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                style={{
                  left: `${hsv[1] * 100}%`,
                  top: `${(1 - hsv[2]) * 100}%`,
                  backgroundColor: currentColor,
                  boxShadow: '0 0 0 2px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.4)',
                }}
              />
            </div>
            
            {/* Hue Slider */}
            <div
              ref={hueRef}
              className="w-5 h-52 rounded-lg cursor-pointer relative"
              style={{
                background: 'linear-gradient(to bottom, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
              }}
              onMouseDown={handleHueMouseDown}
            >
              {/* Hue Handle */}
              <div
                className="absolute w-7 h-3 rounded-sm border-2 border-white shadow-lg transform -translate-x-1/4 -translate-y-1/2 pointer-events-none"
                style={{
                  top: `${hsv[0] * 100}%`,
                  left: '50%',
                  backgroundColor: rgbToHex(...hsvToRgb(hsv[0], 1, 1)),
                  boxShadow: '0 0 0 2px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.4)',
                }}
              />
            </div>
          </div>
          
          {/* Color Preview */}
          <div className="flex items-center gap-3 mt-4">
            <div 
              className="w-10 h-10 rounded-full border-2 border-slate-600 shadow-inner"
              style={{ backgroundColor: currentColor }}
            />
            <div className="flex-1">
              <span className="text-xs text-slate-400">Current</span>
              <div className="font-mono text-sm text-white">{hexInput}</div>
            </div>
          </div>

          {/* RGB Inputs */}
          <div className="flex gap-2 mt-3">
            {['R', 'G', 'B'].map((channel, index) => (
              <div key={channel} className="flex-1">
                <label className="text-xs text-slate-400 block mb-1">{channel}</label>
                <input
                  type="number"
                  min="0"
                  max="255"
                  value={rgb[index]}
                  onChange={(e) => handleRgbChange(index, e.target.value)}
                  className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-sm text-center focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/50"
                />
              </div>
            ))}
          </div>

          {/* Hex Input in Popover */}
          <div className="mt-3">
            <label className="text-xs text-slate-400 block mb-1">HEX</label>
            <input
              type="text"
              value={hexInput}
              onChange={handleHexInputChange}
              onBlur={handleHexInputBlur}
              className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white font-mono text-sm uppercase text-center focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/50"
              placeholder="#000000"
              maxLength={7}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
